import type { BuildLeafFieldInput } from '@cli-types/core-engine/add-field.js'
import {
  FORM_OPTION_FIELD_TYPE_SET,
  FORM_REQUIRED_OPTION_FIELD_TYPES
} from '@cli-constants/schema.js'
import { isFieldType } from '@core-engine/schema/field-type-guards/is-field-type.js'
import { isFormFieldType } from '@core-engine/schema/field-type-guards/is-form-field-type.js'
import { isSupportedAddFieldType } from './is-supported-add-field-type.js'

export function validateLeafFieldInput(input: BuildLeafFieldInput): string[] {
  const errors: string[] = []

  if (!isSupportedAddFieldType(input.kind, input.type)) {
    errors.push(`Field type "${input.type}" is not supported for ${input.kind} schemas.`)
    return errors
  }

  if (input.kind === 'form') {
    if (!isFormFieldType(input.type)) {
      errors.push(`Form field type "${input.type}" is not supported.`)
    }
    if (input.creatable) {
      errors.push('--creatable is only supported for Entity and Single select fields.')
    }
    if (input.relationship) {
      errors.push('Form schemas do not support relationship fields.')
    }
    if (input.multiple && input.type !== 'file') {
      errors.push('--multiple is only supported for file form fields.')
    }
    if (FORM_REQUIRED_OPTION_FIELD_TYPES.has(input.type) && !input.options?.length) {
      errors.push(`Form ${input.type} fields require at least one --option value:Label.`)
    }
    if (input.options?.length && !FORM_OPTION_FIELD_TYPE_SET.has(input.type)) {
      errors.push('--option is only supported for select, radio, and checkbox form fields.')
    }
    return errors
  }

  if (!isFieldType(input.type)) {
    errors.push(`Schema field type "${input.type}" is not supported.`)
  }

  if (input.type === 'select') {
    if (!input.creatable && !input.options?.length) {
      errors.push('Static select fields require at least one --option value:Label.')
    }
  } else if (input.creatable) {
    errors.push('--creatable is only supported for select fields.')
  }

  if (input.multiple && !['select', 'relationship'].includes(input.type)) {
    errors.push('--multiple is only supported for select and relationship fields.')
  }

  if (input.type === 'relationship' && !input.relationship) {
    errors.push('Relationship fields require --relationship <schema>.')
  }

  if (input.relationship && input.type !== 'relationship') {
    errors.push('--relationship is only supported for relationship fields.')
  }

  if (input.options?.length && input.type !== 'select') {
    errors.push('--option is only supported for select fields on Entity and Single schemas.')
  }

  return errors
}
